import React from 'react';
import { Link } from 'react-router-dom';
import { CASE_STUDIES, TEE_DESIGNS } from '../../mock';
import useContent from '../../hooks/useContent';
import MediaSlideshow from './MediaSlideshow';

export const WORK_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'production', label: 'Production' },
  { key: 'live-printing', label: 'Live Printing' },
];

export const kindOf = (p) => (/production/i.test(p.method || '') ? 'production' : 'live-printing');

// Case studies (admin-editable) + tee designs (Production), each tagged with a kind.
export function useWorkItems() {
  const { data: studies } = useContent('case_studies', CASE_STUDIES);
  const tees = TEE_DESIGNS.map((t) => ({ title: t.name, method: 'Production', img: t.src, isTee: true }));
  return [...(studies || []), ...tees].map((p) => ({ ...p, kind: kindOf(p) }));
}

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

function WorkCard({ item }) {
  const Wrap = item.link ? Link : 'div';
  return (
    <Wrap {...(item.link ? { to: item.link } : {})} className="group relative block overflow-hidden rounded-md aspect-[4/5]" data-testid={`work-card-${slug(item.title)}`} data-kind={item.kind}>
      {item.isTee ? (
        <div className="w-full h-full bg-[#00b8ff] flex items-center justify-center p-8">
          <img src={item.img} alt={item.title} loading="lazy" className="max-h-full w-auto object-contain transition-transform duration-700 group-hover:scale-105" />
        </div>
      ) : item.media && item.media.length > 0 ? (
        <MediaSlideshow media={item.media} alt={item.title} />
      ) : (
        <img src={item.img} alt={item.title} loading="lazy" className="w-full h-full object-cover" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 p-6 z-10 pointer-events-none">
        <p className="font-display text-[#f4efe6] uppercase text-2xl md:text-3xl leading-tight tracking-tight">{item.title}</p>
        <span className="inline-block mt-3 text-xs uppercase tracking-[0.2em] text-[#00b8ff]">
          {item.kind === 'production' ? 'Production' : 'Live Printing'}
        </span>
      </div>
    </Wrap>
  );
}

export default function CaseStudies({ items, kind, productionFirst = false, title = 'Project', accent = 'Archives', limit, moreLink, headingLevel = 'h2' }) {
  const all = useWorkItems();
  const base = items || all.filter((p) => !kind || p.kind === kind);
  const ordered = productionFirst ? [...base].sort((a, b) => (b.kind === 'production') - (a.kind === 'production')) : base;
  const list = ordered.slice(0, limit || undefined);
  const H = headingLevel;
  return (
    <section className="relative bg-black py-16 md:py-24" data-testid="work-grid">
      <div className="max-w-[1500px] mx-auto px-6">
        {title && (
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <H className="font-display text-[#f4efe6] uppercase text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              {title} <span className="font-script text-[#00b8ff] normal-case italic">{accent}</span>
            </H>
            {moreLink && (
              <Link to={moreLink.to} className="text-[#f4efe6] font-semibold border-b border-[#00b8ff] pb-1 hover:text-[#00b8ff] transition-colors duration-200 self-start md:self-auto" data-testid="work-more-link">
                {moreLink.label}
              </Link>
            )}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((c) => <WorkCard key={`${c.kind}-${c.title}`} item={c} />)}
        </div>
      </div>
    </section>
  );
}
